import { Injectable, inject } from '@angular/core';
import { IWorkflowRequest, IWorkflowRequestData, WorkflowRequestStatus, WorkflowRequestApprover, IWorkflowRequestApprovalStage, WorkflowExecutionStatus, parseFrontEndObjectProperties } from 'src/app/models/workflow-request.model';
import { ApiDataService } from '../http-data/apidata.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/redux/app.state';
import { NotificationsService } from '../notifications/notifications.service';
import { Observable } from 'rxjs';
import { IActionResult } from '../http-data/apidata.model';
import { environment } from 'src/environments/environment';
import { UserAuthenticationService } from '../user-authentication/user-authentication.service';
import { addMyWorkflowRequest, loadMyWorkflowsRequest, updateMyRequestStageApprove, upsertMyWorkflowExecutionStatus, upsertMyWorkflowRequest } from 'src/app/redux/actions/workflows/my-workflows-request.action';
import { myWorkflowsRequest } from 'src/app/redux/selectors/workflows/my-workflows-request.selector';

@Injectable({
	providedIn: 'root'
})
export class WorkflowRequestService {
	private apiDataService = inject(ApiDataService);
	private store = inject<Store<AppState>>(Store);
	private notificationsService = inject(NotificationsService);
	private userAuthenticationService = inject(UserAuthenticationService);

	/**
	 * Load workflows form the API
	 * ! DO NOT CALL DIRECTLY - ONLY USED IN workflows-request.effects.ts - USE getWorkflowsRequests() from this service
	 * @returns List of Workflows
	 */
	public loadMyWorkflowsRequests(): Observable<IActionResult<IWorkflowRequest[]>> {
		return this.apiDataService.get<IWorkflowRequest[]>(`${environment.urls.hubApi}api/workflows/myRequests`);
	}


	public getMyWorkflowsRequests(): Promise<IWorkflowRequest[]> {
		return new Promise<IWorkflowRequest[]>((resolve, reject) => {
			let resolved = false
			let ranOnce = false
			const storeSubscription = this.redux_getMyWorkflowsRequestSelector().subscribe({
				next: (workflowState) => {
					if (!workflowState) {
						reject();
					} else {
						if (!workflowState.loading) {
							if (workflowState.error) {
								reject();

								resolved = true;
								// If has already ran once then unsbcribe
								if (ranOnce) {
									storeSubscription.unsubscribe();
								}
							} else if (!workflowState.loadedOnce) {
								this.redux_loadMyWorkflowsRequests()
							} else {
								resolve(workflowState.value);

								resolved = true;
								// If has already ran once then unsbcribe
								if (ranOnce) {
									storeSubscription.unsubscribe();
								}
							}
						}
					}
				}
			});

			ranOnce = true;
			// If has values on first run then unsbcribe
			if (resolved) {
				storeSubscription.unsubscribe();
			}
		});
	}

	// Get a workflow by id
	public async getMyWorkflowById(workflowId: string): Promise<IWorkflowRequest | undefined> {
		return (await this.getMyWorkflowsRequests())?.find(x => x.taskId === workflowId)
	}

	public async getWorkflowRequestByIdFromApi(workflowId:string): Promise<IWorkflowRequest | undefined> {
		return new Promise<IWorkflowRequest | undefined>((resolve) => {
			this.apiDataService.get<IWorkflowRequest | undefined>(`${environment.urls.hubApi}api/workflows/${workflowId}/request`).subscribe({
				next: (resp) => {
					resolve(resp.data)
				},
				error: () => {
					resolve(undefined)
				}
			})
		})
	}

	// Get the workflow data details
	public getWorkflowDataById(workflowId: string, taskId: string, securityToken: string): Observable<IActionResult<IWorkflowRequestData>> {
		return this.apiDataService.get<IWorkflowRequestData>(`${environment.urls.hubApi}api/workflows/${workflowId}/${taskId}?securityToken=${securityToken}`);
	}

	public approvallAction(status: WorkflowRequestStatus, securityToken: string, comment: string) {
		return this.apiDataService.putRequest<IWorkflowRequest>(`${environment.urls.hubApi}api/workflows/stage/${status}?st=${securityToken}`, { comment });
	}

	// Get workflow request data for public page
	public getWorkflowRequestData(): Promise<IWorkflowRequestData | undefined> {
		return new Promise<IWorkflowRequestData | undefined>((resolve) => {
			this.apiDataService.get<IWorkflowRequestData>(`${environment.urls.hubApi}api/workflows/requestData`).subscribe({
				next: (resp) => {
					resolve(resp.data)
				},
				error: () => {
					resolve(undefined)
				}
			})
		})
	}

	// Get workflow request for public page
	public getWorkflowRequest(): Promise<IWorkflowRequest | undefined> {
		return new Promise<IWorkflowRequest | undefined>((resolve) => {
			this.apiDataService.get<IWorkflowRequest>(`${environment.urls.hubApi}api/workflows/request`).subscribe({
				next: (resp) => {
					resolve(parseFrontEndObjectProperties(resp.data))
				},
				error: () => {
					resolve(undefined)
				}
			})
		})
	}

	//#region WORKFLOW NOTIFICATIONS
	public async notifyApprover(workflowRequest: IWorkflowRequest) {
		// The current stage will be the first stage that is pending in the array of stages
		const currentStage = workflowRequest.stages.find(stage => stage.status === WorkflowRequestStatus.Pending);
		if (currentStage) {
			let userId = "";

			if (location.pathname.startsWith("/catalog/") || location.pathname.startsWith("/tenant/") ){
				userId = this.userAuthenticationService.getPublicCatalogUserAuthenticatedUserId();
			}
			else {
				userId = this.userAuthenticationService.getAuthenticatedUserId();
			}

			const approver = currentStage.approvers.find(approver => approver.id === userId);
			if (approver) {
				this.notificationsService.showInfo(`A new ${workflowRequest.workspacePlatform?.toLowerCase() == "sharepoint" ? "site" : "team"} request requires your approval. Click to review and take action.`, "You have a new approval request!", -1, "", ['view-requests'], workflowRequest.taskId);
			}
		}
	}

	public notifyRequester(workflowRequest: IWorkflowRequest){

		let userId = "";
		if (location.pathname.startsWith("/catalog/")){
			userId = this.userAuthenticationService.getPublicCatalogUserAuthenticatedUserId();
		}
		else {
			userId = this.userAuthenticationService.getAuthenticatedUserId();
		}

		if (workflowRequest.requester.id === userId){
			switch(workflowRequest.status){
				case WorkflowRequestStatus.Completed:
					this.notificationsService.showInfo(`Your ${workflowRequest.workspacePlatform?.toLowerCase() == "sharepoint" ? "site" : "team"} request for the workspace "${workflowRequest.workspaceName}" has been approved. Installation will start soon.`, "Request approved", -1)
					break;
				case WorkflowRequestStatus.Rejected:
					this.notificationsService.showInfo(`Your ${workflowRequest.workspacePlatform?.toLowerCase() == "sharepoint" ? "site" : "team"} request for the workspace "${workflowRequest.workspaceName}" has been rejected.`, "Request rejected", -1)
					break;
				default:
					break;
			}
		}
	}
	//#endregion


	//#region STORE OPERATIONS
	//! These are private methods of this service. All store operations should be handled inside this service

	public redux_getMyWorkflowsRequestSelector() {
		return this.store.select(myWorkflowsRequest)
	}

	private redux_loadMyWorkflowsRequests() {
		this.store.dispatch(loadMyWorkflowsRequest())
	}

	public redux_addMyWorkflowRequests(workflow: IWorkflowRequest, setLoaded = false) {
		this.store.dispatch(addMyWorkflowRequest({ workflow, setLoaded }))
	}

	public upsertMyWorkflowRequest(workflow: IWorkflowRequest) {
		this.store.dispatch(upsertMyWorkflowRequest({ workflow }))
	}

	public redux_updateMyRequestStageApprove(workflow: IWorkflowRequest, approver: WorkflowRequestApprover, stage: IWorkflowRequestApprovalStage, nextStage: IWorkflowRequestApprovalStage | undefined) {
		this.store.dispatch(updateMyRequestStageApprove({ workflow, approver, stage, nextStage }));
	}

	public redux_updateExecutionStatusMyWorkflow(taskId: string, executionStatus: WorkflowExecutionStatus) {
		this.store.dispatch(upsertMyWorkflowExecutionStatus({ taskId, executionStatus }))
	}

	//#endregion

	public static shouldUpdateMyWorkflows(workflow: IWorkflowRequest, userIsOwner: boolean, userId: string, userEmail: string): boolean {
		if (userIsOwner) {
			// Check if the user is the requester or an approver
			return workflow.requester.id === userId || workflow.requester.email == userEmail ||
				workflow.stages.some(stage =>
					stage.approvers.some(approver => approver.id === userId || approver.email === userEmail));
		}
		// If not the owner, only update if the user is the requester
		return workflow.requester.id === userId || workflow.requester.email === userEmail;
	}
}
